import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Container, Box, Tabs, Tab, IconButton, Drawer, List, ListItem, ListItemText, Divider, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import EmployeeManagement from './EmployeeManagement';
import AttendanceView from './AttendanceView';
import SalarySlipView from './SalarySlipView';
import LeaveApplication from './LeaveApplication';
import Login from './Login';
import UserManagement from './UserManagement';

function App() {
  const [user, setUser] = React.useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  });
  const [page, setPage] = React.useState('employees');
  const [empTab, setEmpTab] = React.useState('salary');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const navTabs = [
    { label: 'Employee Management', value: 'employees' },
    { label: 'Attendance', value: 'attendance' },
    { label: 'Salary Slips', value: 'salary' },
    { label: 'Leave Application', value: 'leave' },
  ];

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  // Employee: Only show SalarySlipView and LeaveApplication
  if (user.role === 'employee') {
    return (
      <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        <AppBar position="static" color="primary" sx={{ boxShadow: 2 }}>
          <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              sx={{ fontWeight: 700, letterSpacing: 2, fontSize: isMobile ? 18 : 24 }}
            >
              <span style={{ fontFamily: 'serif', fontSize: isMobile ? 22 : 32, marginRight: 8 }}>TBT</span> TextBox Technology
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 2, fontWeight: 500, fontSize: isMobile ? 14 : 16 }}>Welcome, {user.username}</Typography>
              <Button color="inherit" onClick={handleLogout} sx={{ fontSize: isMobile ? 12 : 16 }}>Logout</Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Container maxWidth={isMobile ? 'sm' : 'xl'} sx={{ mt: 4, px: isMobile ? 1 : 3 }}>
          <Tabs
            value={empTab}
            onChange={(_, v) => setEmpTab(v)}
            textColor="inherit"
            indicatorColor="secondary"
            sx={{ minHeight: 48, mb: 2 }}
          >
            <Tab key="salary" label="Salary Slips" value="salary" sx={{ fontWeight: 500, minWidth: 120 }} />
            <Tab key="leave" label="Leave Application" value="leave" sx={{ fontWeight: 500, minWidth: 120 }} />
          </Tabs>
          {empTab === 'salary' && <SalarySlipView user={user} />}
          {empTab === 'leave' && <LeaveApplication user={user} />}
        </Container>
      </Box>
    );
  }

  // Admin: Show all features
  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar position="static" color="primary" sx={{ boxShadow: 2 }}>
        <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            sx={{ fontWeight: 700, letterSpacing: 2, fontSize: isMobile ? 18 : 24 }}
          >
            <span style={{ fontFamily: 'serif', fontSize: isMobile ? 22 : 32, marginRight: 8 }}>TBT</span> TextBox Technology
          </Typography>
          {isMobile ? (
            <>
              <IconButton color="inherit" edge="end" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 220 }} role="presentation" onClick={() => setDrawerOpen(false)}>
                  <List>
                    {navTabs.map(tab => (
                      <ListItem button key={tab.value} onClick={() => setPage(tab.value)}>
                        <ListItemText primary={tab.label} />
                      </ListItem>
                    ))}
                    {user.role === 'admin' && (
                      <ListItem button onClick={() => setPage('users')}>
                        <ListItemText primary="Users" />
                      </ListItem>
                    )}
                  </List>
                  <Divider />
                  <List>
                    <ListItem>
                      <Typography sx={{ fontWeight: 500, fontSize: 14 }}>Welcome, {user.username}</Typography>
                    </ListItem>
                    <ListItem button onClick={handleLogout}>
                      <ListItemText primary="Logout" />
                    </ListItem>
                  </List>
                </Box>
              </Drawer>
            </>
          ) : (
            <>
              <Tabs
                value={page}
                onChange={(_, v) => setPage(v)}
                textColor="inherit"
                indicatorColor="secondary"
                sx={{ minHeight: 48 }}
              >
                {navTabs.map(tab => (
                  <Tab
                    key={tab.value}
                    label={tab.label}
                    value={tab.value}
                    sx={{ fontWeight: 500, minWidth: 120 }}
                  />
                ))}
                {user.role === 'admin' && (
                  <Tab label="Users" value="users" />
                )}
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 2, fontWeight: 500 }}>Welcome, {user.username}</Typography>
                <Button color="inherit" onClick={handleLogout}>Logout</Button>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth={isMobile ? 'sm' : 'xl'} sx={{ mt: 4, px: isMobile ? 1 : 3 }}>
        {page === 'employees' && <EmployeeManagement user={user} />}
        {page === 'attendance' && <AttendanceView user={user} />}
        {page === 'salary' && <SalarySlipView user={user} />}
        {page === 'leave' && <LeaveApplication user={user} />}
        {page === 'users' && user.role === 'admin' && <UserManagement />}
      </Container>
    </Box>
  );
}

export default App;
